import { Logger } from '@/utils/logging';

const DATA_ATTR = 'data-squire-ga-id';

// Accept only Google's documented measurement-id formats. Anything else is
// rejected before being embedded into a <script> tag, so a misconfigured or
// hostile brand-settings value can't inject JS into the booking page.
const GA_ID_PATTERN = /^(G|GTM)-[A-Z0-9]+$/i;

const removeAllSquireGaScripts = (): void => {
  const existing = document.querySelectorAll(`script[${DATA_ATTR}]`);
  existing.forEach((node) => node.parentNode?.removeChild(node));
};

const isCurrentlyInjected = (gaId: string): boolean => {
  return !!document.querySelector(`script[${DATA_ATTR}="${gaId}"]`);
};

const injectScripts = (gaId: string): void => {
  const loaderScript = document.createElement('script');
  loaderScript.async = true;
  loaderScript.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}&l=squireCustomBrandGaScript`;
  loaderScript.setAttribute(DATA_ATTR, gaId);
  document.head.appendChild(loaderScript);

  const initScript = document.createElement('script');
  initScript.setAttribute(DATA_ATTR, gaId);
  initScript.textContent = `
    window.squireCustomBrandGaScript = window.squireCustomBrandGaScript || [];
    function squireCustomBrandGaGtag(){squireCustomBrandGaScript.push(arguments);}
    window.squireCustomBrandGaGtag = squireCustomBrandGaGtag;
    squireCustomBrandGaGtag('js', new Date());
    squireCustomBrandGaGtag('config', '${gaId}');
  `;
  document.head.appendChild(initScript);
};

/**
 * Idempotent script-tag sync. Call repeatedly with the current measurement id
 * (or null to clear). When called with a different id than what's currently
 * injected, removes the old tags before injecting new ones.
 */
export const syncGaScriptTags = (gaId: string | null): void => {
  if (typeof document === 'undefined') return;
  // Shops without a GA id come back as '' rather than null.
  if (gaId === null || gaId.trim() === '') {
    removeAllSquireGaScripts();
    return;
  }
  if (!GA_ID_PATTERN.test(gaId)) {
    Logger.warn('[ga-tracker] rejected malformed gaId', { gaId });
    removeAllSquireGaScripts();
    return;
  }
  if (isCurrentlyInjected(gaId)) return;
  removeAllSquireGaScripts();
  injectScripts(gaId);
};
